import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ROLE_LABELS } from "@/lib/roles";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Settings = () => {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.new_password !== form.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated. Please sign in again.");
      setForm({ current_password: "", new_password: "", confirm: "" });
      setTimeout(logout, 1500);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Account" title="Profile & Settings" description="Your profile information, password and academy preferences." />
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F]">Profile</div>
          <div className="mt-4 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center font-display text-xl">{user?.avatar_initials || "U"}</div>
            <div>
              <div className="font-display text-2xl text-[#1B2C5A]">{user?.name}</div>
              <div className="text-sm text-[#475569]">{user?.email}</div>
              <div className="text-xs text-[#9DCD3F] mt-1">{ROLE_LABELS[user?.role]}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F]">Security</div>
          <h3 className="font-display text-2xl text-[#1B2C5A] mt-2 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#9DCD3F]" /> Change Password
          </h3>
          <p className="text-sm text-[#475569] mt-2">Update your account password. You'll be signed out after a successful change.</p>
          <form onSubmit={submit} className="mt-5 space-y-4" data-testid="change-password-form">
            <div>
              <Label className="text-[#1B2C5A] text-sm">Current Password</Label>
              <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1" data-testid="cpw-current" />
            </div>
            <div>
              <Label className="text-[#1B2C5A] text-sm">New Password (min 8 chars)</Label>
              <Input type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1" data-testid="cpw-new" />
            </div>
            <div>
              <Label className="text-[#1B2C5A] text-sm">Confirm New Password</Label>
              <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1" data-testid="cpw-confirm" />
            </div>
            <Button type="submit" disabled={saving} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="cpw-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</> : "Update Password"}
            </Button>
          </form>
        </Card>

        <Card className="p-6 border-[#E2E8F0] lg:col-span-2">
          <div className="overline text-[#9DCD3F]">Academy</div>
          <h3 className="font-display text-2xl text-[#1B2C5A] mt-2">Darussalam Foundation Academy</h3>
          <p className="text-[#475569] mt-1 font-serif italic text-sm">أكاديمية دار السلام الإسلامية</p>
          <p className="text-[#9DCD3F] mt-1 text-xs uppercase tracking-widest">Motto: Knowledge & Action</p>
          <ul className="mt-4 text-sm text-[#475569] space-y-1.5">
            <li>• Role-based access control across 9 roles</li>
            <li>• Audit logs for every change</li>
            <li>• Daily automatic cloud backup</li>
            <li>• bcrypt-encrypted credentials & JWT-based sessions</li>
          </ul>
          <div className="mt-5 pt-5 border-t border-[#E2E8F0] text-xs text-[#475569] space-y-1">
            <p>📞 +234 703 616 1413 · +234 700 315 8938</p>
            <p>🌐 www.dfa.com.ng</p>
            <p>📍 Shehu Idris Road, Emanto by Kabama Layout, Zaria, Kaduna State, Nigeria.</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
